import React from "react";
import { styled } from "@glitz/react";
import {
  black,
  darkGrey,
  fontSize,
  fontWeight,
  green,
  grey,
  mediumGrey,
  spacing,
  white,
} from "../../theme";

type Props = {
  label?: string;
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /** Visuell variant: pill med +/- eller enkel input. Default: "stepper". */
  variant?: "stepper" | "plain";
  /** Visa label ovanför (default) eller center (kompakt). */
  labelPlacement?: "top" | "center";
  ariaLabel?: string;
  /** Valfri suffix-text som t.ex. 'kg' eller 'km/h'. */
  suffix?: string;
  id?: string;
};

const clamp = (n: number, min?: number, max?: number) => {
  let v = n;
  if (min != null) v = Math.max(min, v);
  if (max != null) v = Math.min(max, v);
  return v;
};

const NumberField: React.FC<Props> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled,
  variant = "stepper",
  labelPlacement = "top",
  ariaLabel,
  suffix,
  id,
}) => {
  const decrease = () => {
    if (disabled) return;
    onChange(clamp(value - step, min, max));
  };
  const increase = () => {
    if (disabled) return;
    onChange(clamp(value + step, min, max));
  };
  const atMin = min != null && value <= min;
  const atMax = max != null && value >= max;

  if (variant === "plain") {
    return (
      <PlainWrap>
        {label ? <PlainLabel htmlFor={id}>{label}</PlainLabel> : null}
        <PlainRow>
          <PlainInput
            id={id}
            type="number"
            inputMode="numeric"
            value={value}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            aria-label={ariaLabel ?? label}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              if (!Number.isFinite(v)) return;
              onChange(clamp(v, min, max));
            }}
          />
          {suffix ? <SuffixText>{suffix}</SuffixText> : null}
        </PlainRow>
      </PlainWrap>
    );
  }

  return (
    <StepperWrap>
      {label && labelPlacement === "top" ? (
        <StepperLabel>{label}</StepperLabel>
      ) : null}
      <StepperShell>
        <StepperBtn
          type="button"
          disabled={disabled || atMin}
          aria-label={`Minska ${label ?? ariaLabel ?? ""}`}
          onClick={decrease}
        >
          −
        </StepperBtn>
        <StepperValue aria-live="polite">
          {value}
          {suffix ? <Suffix>{suffix}</Suffix> : null}
        </StepperValue>
        <StepperBtn
          type="button"
          disabled={disabled || atMax}
          aria-label={`Öka ${label ?? ariaLabel ?? ""}`}
          onClick={increase}
        >
          +
        </StepperBtn>
      </StepperShell>
      {label && labelPlacement === "center" ? (
        <StepperLabelCenter>{label}</StepperLabelCenter>
      ) : null}
    </StepperWrap>
  );
};

export default NumberField;

const StepperWrap = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.xs,
  flex: { grow: 1, shrink: 1, basis: "0" },
  minWidth: "min(100%, 140px)",
});

const StepperLabel = styled.span({
  fontSize: fontSize.xxs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "center",
});

const StepperLabelCenter = styled.span({
  fontSize: fontSize.xxs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textAlign: "center",
});

/**
 * Slim, rektangulär stepper med 8px-radie. Följer produktsidans
 * quantity selector: +/- på sidorna separerade med tunna skiljelinjer.
 */
const StepperShell = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "stretch",
  width: "100%",
  maxWidth: "180px",
  height: "36px",
  backgroundColor: white,
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "8px",
  overflow: "hidden",
  boxSizing: "border-box",
});

const StepperBtn = styled.button({
  flexShrink: 0,
  width: "36px",
  border: { xy: { style: "none", width: 0, color: "transparent" } },
  backgroundColor: mediumGrey,
  cursor: "pointer",
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
  padding: { xy: 0 },
  lineHeight: 1,
  transitionProperty: "background-color, color",
  transitionDuration: "0.15s",
  ":hover": {
    backgroundColor: black,
    color: white,
  },
  ":disabled": {
    opacity: 0.35,
    cursor: "not-allowed",
    backgroundColor: mediumGrey,
    color: darkGrey,
  },
});

const StepperValue = styled.span({
  flex: { grow: 1, shrink: 1 },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: spacing.xxs,
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
  minWidth: "2ch",
  backgroundColor: white,
  border: {
    left: { style: "solid", color: grey, width: "1px" },
    right: { style: "solid", color: grey, width: "1px" },
  },
});

const Suffix = styled.span({
  fontSize: fontSize.xs,
  color: darkGrey,
  fontWeight: fontWeight.regular,
});

const PlainWrap = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  width: "100%",
});

const PlainLabel = styled.label({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: darkGrey,
});

const PlainRow = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
});

const PlainInput = styled.input({
  padding: { xy: spacing.s },
  fontSize: fontSize.m,
  border: { xy: { style: "solid", color: black, width: "1px" } },
  borderRadius: "4px",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: white,
  color: black,
  transitionProperty: "border-color",
  transitionDuration: "0.2s",
  transitionTimingFunction: "ease",
  ":focus": {
    outline: { style: "none" },
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
  ":disabled": {
    backgroundColor: mediumGrey,
    color: darkGrey,
    cursor: "not-allowed",
  },
});

const SuffixText = styled.span({
  fontSize: fontSize.s,
  color: darkGrey,
  fontWeight: fontWeight.bold,
  flexShrink: 0,
});
